import React from 'react';
import PropTypes from 'prop-types';
import { ButtonGroup, Button, Col, Row, Nav } from 'react-bootstrap';
import { Route, Switch, Redirect } from 'react-router-dom';
import { withTracker } from 'meteor/react-meteor-data';
import { Meteor } from 'meteor/meteor';
import { Bert } from 'meteor/themeteorchef:bert';
import Authorized from '../../components/Authorized';
import { getName, isDeleted, isThematic, is3D } from '../../../api/Layers/layer-operations';
import Layers from '../../../api/Layers/Layers';

import handleErrorInClient from '../../../modules/handle-error-in-client';
import methodPromise from '../../../modules/method-promise-wrapper';

import PageHeader from '../../atoms/PageHeader';

import Loading from '../../components/Loading';

import MapContainerViewLayer from '../../containers/MapContainerViewLayer';
import LinkContainer from '../../containers/LinkContainer';
import TableListContainer from '../../containers/TableListContainer';

import NotFound from '../NotFound';
import Styles from '../Styles';
import EditStyle from '../EditStyle';
import NewStyle from '../NewStyle';
import Graphics from '../Graphics';
import ViewGraphic from '../ViewGraphic';
import NewGraphic from '../NewGraphic';
import EditGraphic from '../EditGraphic';
import ViewThematicLayerTable from '../ViewThematicLayerTable';
import CesiumViewerContainerViewLayer from '../../containers/CesiumViewerContainerViewLayer';
import TableComponent from '../../components/TableComponent';

const handleRemove = async (layerId, history) => {
  try {
    await methodPromise('layers.softDelete', layerId);
    Bert.alert('Layer deleted!', 'warning');
    history.push(`/layers`);
  } catch (error) {
    handleErrorInClient(error);
  }
};

const LayerMap = ({ layer, ...rest }) => (
  <Row noGutters>
    <Col xs={12}>
      {is3D(layer) && <CesiumViewerContainerViewLayer layer={layer} {...rest} />}
      {!isThematic(layer) && !is3D(layer) && <MapContainerViewLayer layer={layer} {...rest} />}
      {isThematic(layer) && <ViewThematicLayerTable layer={layer} />}
    </Col>
  </Row>
);

LayerMap.propTypes = {
  layer: PropTypes.object.isRequired
};

const ViewLayer = ({ loading, layer, match, history, defaultBaseMap, ...rest }) => {
  if (loading) return <Loading />;
  if (isDeleted(layer)) return <NotFound />;
  return (
    <>
      <PageHeader>
        <h4 className="mr-auto">{getName(layer)}</h4>
        <Nav variant="pills">
          <LinkContainer to={`${match.url}/view`}>
            <Nav.Link>{!isThematic(layer) ? 'Map' : 'Data'}</Nav.Link>
          </LinkContainer>
          {!isThematic(layer) && (
            <>
              <LinkContainer to={`${match.url}/styles`}>
                <Nav.Link active={false}>Styles</Nav.Link>
              </LinkContainer>
              <LinkContainer to={`${match.url}/data`}>
                <Nav.Link active={false}>Data</Nav.Link>
              </LinkContainer>
            </>
          )}
          <LinkContainer to={`${match.url}/graphics`}>
            <Nav.Link active={false}>Graphics</Nav.Link>
          </LinkContainer>
        </Nav>
        <ButtonGroup size="sm">
          <Button variant="outline-info" onClick={() => history.push(`${match.url}/edit`)}>
            Edit
          </Button>
          <Button onClick={() => handleRemove(layer._id, history)} variant="outline-danger">
            Delete
          </Button>
        </ButtonGroup>
      </PageHeader>
      <Switch>
        <Route
          exact
          path="/layers/:layer_id/view"
          render={routeProps => (
            <LayerMap layer={layer} defaultBaseMap={defaultBaseMap} {...routeProps} {...rest} />
          )}
        />
        <Route
          exact
          path="/layers/:layer_id/styles"
          render={routeProps => (
            <TableListContainer
              Page={props => (
                <Styles
                  layer={layer}
                  defaultBaseMap={defaultBaseMap}
                  {...props}
                  {...routeProps}
                  {...rest}
                />
              )}
            />
          )}
        />
        {/* <Route
          exact
          path="/layers/:layer_id/styles/new"
          render={routeProps => (
            <NewStyle match={match} history={history} {...routeProps} {...rest} />
          )}
        /> */}

        <Authorized
          exact
          allowedRoles={['basic-user', 'premium-user', 'admin']}
          path="/layers/:layer_id/styles/new"
          pathAfterFailure="/"
          match={match}
          component={NewStyle}
          history={history}
          {...rest}
        />
        <Route
          exact
          path="/layers/:layer_id/styles/:style_id"
          render={routeProps => (
            <EditStyle match={match} history={history} {...routeProps} {...rest} />
          )}
        />
        <Route
          exact
          path="/layers/:layer_id/data"
          render={routeProps => (
            <TableComponent layer={layer} rowsInitial={15} {...routeProps} {...rest} />
          )}
        />
        <Route
          exact
          path="/layers/:layer_id/graphics"
          render={routeProps => (
            <TableListContainer
              Page={props => <Graphics layer={layer} {...props} {...routeProps} {...rest} />}
            />
          )}
        />
        <Authorized
          exact
          allowedRoles={['basic-user', 'premium-user', 'admin']}
          path="/layers/:layer_id/graphics/new"
          pathAfterFailure="/"
          component={NewGraphic}
          match={match}
          history={history}
          {...rest}
        />
        <Route
          exact
          path="/layers/:layer_id/graphics/:graphic_id/edit"
          render={routeProps => (
            <EditGraphic match={match} history={history} {...routeProps} {...rest} />
          )}
        />
        <Route
          exact
          path="/layers/:layer_id/graphics/:graphic_id"
          render={props =>
            React.createElement(ViewGraphic, {
              match,
              history,
              ...props
            })
          }
        />
        <Route
          exact
          path="/layers/:layer_id/"
          render={() => (
            <Redirect
              to={`${match.url}${match.url.charAt(match.url.length - 1) === '/' ? '' : '/'}view`}
            />
          )}
        />
        <Route component={NotFound} />
      </Switch>
    </>
  );
};

ViewLayer.defaultProps = {
  layer: {},
  defaultBaseMap: {}
};

ViewLayer.propTypes = {
  loading: PropTypes.bool.isRequired,
  layer: PropTypes.object,
  defaultBaseMap: PropTypes.object,
  match: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired
};

export default withTracker(({ match, userControlSetting }) => {
  const layerId = match.params.layer_id;
  const layersSub = Meteor.subscribe('layers.view', layerId);

  return {
    loading: !layersSub.ready(),
    layer: Layers.findOne(layerId),
    defaultBaseMap: Layers.findOne(userControlSetting.defaultBaseMap)
  };
})(ViewLayer);
